/**
 * Netcode debug overlay.
 *
 * Not decoration. The lessons doc's central process point is that
 * interpolation quality and clock agreement are *perceptual* bugs that unit
 * tests cannot see — which means the only way to know the netcode is healthy
 * is to be able to look at it while playing. Prediction error in particular
 * should sit flat at zero; a graph that is visibly alive means client and
 * server have stopped agreeing, and that is worth noticing in the second it
 * starts rather than in the week someone complains about rubber-banding.
 */

const HISTORY = 240;
const GRAPH_WIDTH = 240;
const GRAPH_HEIGHT = 48;
/** Full-scale deflection on the error graph. */
const GRAPH_MAX_ERROR = 0.25;

export interface OverlayStats {
  status: string;
  detail: string;
  fps: number;
  drawCalls: number;
  triangles: number;
  rttMs: number;
  clockOffsetMs: number;
  clockSamples: number;
  serverTick: number;
  /**
   * The phase the movement gate is using, and the team it is gating.
   *
   * Here rather than only on the player HUD because the phase transition is
   * the one event that used to break prediction, and a harness that cannot see
   * the transition cannot tell a fixed run from a run that never reached one.
   * That is not hypothetical: the 30s measurement window and the 30s Hiding
   * phase are the same length, so the release landed inside the samples about
   * one run in four — which is exactly the rate at which the bug was
   * "intermittent".
   */
  gatePhase: string;
  myTeam: string;
  snapshotsPerSecond: number;
  predictionErrorM: number;
  pendingInputs: number;
  lastReplayCount: number;
  snapCount: number;
  correctionCount: number;
  correctionM: number;
  interpBufferDepth: number;
  starvedEntities: number;
  entityCount: number;
  kbPerSecondUp: number;
  kbPerSecondDown: number;
  conditions: string;
  /** The simulated body's own position — `renderPose`, not the free-fly
   *  spectator camera, so this reads the same whether or not the local
   *  player is currently found. */
  posX: number;
  posY: number;
  posZ: number;
}

export class DebugOverlay {
  private readonly text: HTMLPreElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly errors: number[] = [];
  private visible = true;

  constructor(root: HTMLElement) {
    const panel = document.createElement("div");
    panel.className = "debug-panel";

    this.text = document.createElement("pre");
    panel.appendChild(this.text);

    this.canvas = document.createElement("canvas");
    this.canvas.width = GRAPH_WIDTH;
    this.canvas.height = GRAPH_HEIGHT;
    this.canvas.className = "debug-graph";
    panel.appendChild(this.canvas);

    const caption = document.createElement("div");
    caption.className = "debug-caption";
    caption.textContent = `prediction error (full scale ${GRAPH_MAX_ERROR}m) — F3 to hide`;
    panel.appendChild(caption);

    root.appendChild(panel);
    this.ctx = this.canvas.getContext("2d");

    window.addEventListener("keydown", (event) => {
      if (event.code !== "F3") return;
      event.preventDefault();
      this.visible = !this.visible;
      panel.style.display = this.visible ? "block" : "none";
    });
  }

  update(stats: OverlayStats): void {
    this.errors.push(stats.predictionErrorM);
    if (this.errors.length > HISTORY) this.errors.shift();
    if (!this.visible) return;

    this.text.textContent = [
      `${stats.status.toUpperCase()}${stats.detail ? `  ${stats.detail}` : ""}`,
      `sim   ${stats.conditions}`,
      ``,
      `fps   ${stats.fps.toFixed(0).padStart(5)}    tick  ${String(stats.serverTick).padStart(6)}`,
      `rtt   ${stats.rttMs.toFixed(0).padStart(5)}ms  snap/s${stats.snapshotsPerSecond.toFixed(1).padStart(6)}`,
      `clock ${stats.clockOffsetMs.toFixed(0).padStart(5)}ms  samples${String(stats.clockSamples).padStart(4)}`,
      `draws ${String(stats.drawCalls).padStart(5)}    tris  ${String(stats.triangles).padStart(6)}`,
      `phase ${stats.gatePhase.padStart(8)}  team ${stats.myTeam}`,
      `pos   ${stats.posX.toFixed(2)} ${stats.posY.toFixed(2)} ${stats.posZ.toFixed(2)}`,
      ``,
      `pred err  ${stats.predictionErrorM.toFixed(4).padStart(8)} m`,
      `correction${stats.correctionM.toFixed(4).padStart(8)} m`,
      `replayed  ${String(stats.lastReplayCount).padStart(8)} inputs`,
      `unacked   ${String(stats.pendingInputs).padStart(8)} inputs`,
      `snaps/corr${String(stats.snapCount).padStart(5)}/${stats.correctionCount}`,
      ``,
      `entities  ${String(stats.entityCount).padStart(8)}`,
      `interp buf${String(stats.interpBufferDepth).padStart(8)} samples`,
      `starved   ${String(stats.starvedEntities).padStart(8)}`,
      ``,
      `up   ${stats.kbPerSecondUp.toFixed(1).padStart(6)} kB/s`,
      `down ${stats.kbPerSecondDown.toFixed(1).padStart(6)} kB/s`,
    ].join("\n");

    this.drawGraph();
  }

  private drawGraph(): void {
    const ctx = this.ctx;
    if (ctx === null) return;

    ctx.clearRect(0, 0, GRAPH_WIDTH, GRAPH_HEIGHT);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 0, GRAPH_WIDTH, GRAPH_HEIGHT);

    // The line below which corrections are ignored entirely. Traffic that
    // stays under it is healthy.
    ctx.strokeStyle = "rgba(120,200,140,0.5)";
    ctx.beginPath();
    const epsilonY = GRAPH_HEIGHT - (0.02 / GRAPH_MAX_ERROR) * GRAPH_HEIGHT;
    ctx.moveTo(0, epsilonY);
    ctx.lineTo(GRAPH_WIDTH, epsilonY);
    ctx.stroke();

    if (this.errors.length < 2) return;

    ctx.strokeStyle = "#7fd1ff";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i < this.errors.length; i++) {
      const x = (i / (HISTORY - 1)) * GRAPH_WIDTH;
      const clamped = Math.min(this.errors[i]!, GRAPH_MAX_ERROR);
      const y = GRAPH_HEIGHT - (clamped / GRAPH_MAX_ERROR) * GRAPH_HEIGHT;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}
